import { isNavigatorOnline } from './upload-retry';

// New Relic event type the "upload failures" alert queries (FROM UploadFailure).
export const UPLOAD_FAILURE_EVENT = 'UploadFailure';

const MAX_MESSAGE_LENGTH = 255;

export type UploadFailureAttributes = Record<string, string | number | boolean>;

interface UploadFailureInput {
  error: any;
  signedUrl?: string;
  uploadEndpoint?: string;
  collectionId?: string;
  galleryId?: string;
  userId?: string;
  file?: { size?: number; type?: string };
  failureCount?: number;
}

// Only the host: the signed URL's query string is a credential and must not leave the browser.
const getProviderHost = (signedUrl?: string): string => {
  try {
    return signedUrl ? new URL(signedUrl).host : '';
  } catch {
    return '';
  }
};

// S3-compatible providers answer with an XML body like <Error><Code>InternalError</Code>...
const getProviderErrorCode = (data: unknown): string => {
  if (typeof data !== 'string') return '';

  return data.match(/<Code>([^<]*)<\/Code>/)?.[1] ?? '';
};

export const buildUploadFailureAttributes = ({
  error,
  signedUrl,
  uploadEndpoint,
  collectionId,
  galleryId,
  userId,
  file,
  failureCount
}: UploadFailureInput): UploadFailureAttributes => ({
  uploadType: 'galleryImage',
  environment: process.env.REACT_APP_ENVIRONMENT || 'local',
  statusCode: error?.response?.status ?? 0,
  errorCode: error?.code ?? '',
  errorMessage: String(error?.message ?? error ?? '').slice(0, MAX_MESSAGE_LENGTH),
  providerErrorCode: getProviderErrorCode(error?.response?.data),
  providerHost: getProviderHost(signedUrl),
  uploadEndpoint: uploadEndpoint ?? '',
  collectionId: collectionId ?? '',
  galleryId: galleryId ?? '',
  userId: userId ?? '',
  fileSize: file?.size ?? 0,
  fileType: file?.type ?? '',
  failureCount: failureCount ?? 0,
  online: isNavigatorOnline()
});

// Never throws: monitoring must not break the upload saga.
export const reportUploadFailure = (error: unknown, attributes: UploadFailureAttributes) => {
  try {
    const newrelic = typeof window !== 'undefined' ? window.newrelic : undefined;
    if (!newrelic) return;

    if (newrelic.recordCustomEvent) {
      newrelic.recordCustomEvent(UPLOAD_FAILURE_EVENT, attributes);
    } else if (newrelic.addPageAction) {
      newrelic.addPageAction(UPLOAD_FAILURE_EVENT, attributes);
    }

    newrelic.noticeError?.(
      error instanceof Error ? error : new Error(String(attributes.errorMessage)),
      attributes
    );
  } catch (e) {
    console.error('reportUploadFailure', e);
  }
};
